<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useSongStore } from '../../stores/songStore'
import { useUserStore } from '../../stores/userStore'
import { parsePerformance, type PerformanceLine, type PerformanceSegment, type SegmentType } from '../../utils/performance'

const props = defineProps<{
  songId: number
}>()

const emit = defineEmits(['back'])

const songStore = useSongStore()
const userStore = useUserStore()
const song = computed(() => songStore.currentSong)
const loading = computed(() => songStore.detailLoading)

// Teleprompter settings
const delay = ref(3)
const speed = ref(3)
const isPlaying = ref(false)
const scrollContainer = ref<HTMLElement | null>(null)
const contentEl = ref<HTMLElement | null>(null)
const scaleValue = ref(1)
const scaledHeight = ref(0)
let scrollInterval: ReturnType<typeof setInterval> | null = null
let delayTimeout: ReturnType<typeof setTimeout> | null = null
let scrollAccumulator = 0
let resizeObs: ResizeObserver | null = null

// Fit the monospace content block to the container width by scaling.
// We measure at scale=1 (whitespace-pre, so the content reports its true
// natural width), then apply a transform: scale() to shrink it to fit.
// A sizer div reserves the post-scale height so vertical scrolling still
// reflects the visual layout.
const recomputeScale = () => {
  const content = contentEl.value
  const container = scrollContainer.value
  if (!content || !container) return
  // Temporarily clear the transform so scrollWidth/scrollHeight report
  // the natural (unscaled) dimensions.
  const prev = content.style.transform
  content.style.transform = 'none'
  const naturalWidth = content.scrollWidth
  const naturalHeight = content.scrollHeight
  content.style.transform = prev
  if (naturalWidth <= 0 || naturalHeight <= 0) return
  const styles = window.getComputedStyle(container)
  const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
  const avail = container.clientWidth - padX
  const s = Math.min(1, avail / naturalWidth)
  scaleValue.value = s
  scaledHeight.value = naturalHeight * s
}

onMounted(async () => {
  await songStore.fetchSongDetails(props.songId)
  if (song.value) {
    delay.value = song.value.teleprompterDelay || 3
    speed.value = song.value.teleprompterSpeed || 3
  }
  await nextTick()
  recomputeScale()
  if (scrollContainer.value) {
    resizeObs = new ResizeObserver(() => recomputeScale())
    resizeObs.observe(scrollContainer.value)
  }
})

watch(song, (s) => {
  if (s) {
    delay.value = s.teleprompterDelay ?? 3
    speed.value = s.teleprompterSpeed || 3
  }
})

// Teleprompter-only: squeeze runs of 3+ whitespace in lyric lines down to
// 2 spaces. Runs of whitespace are identified on the lyric line; the chord
// line above is column-compressed in lockstep so chords stay aligned with
// the same lyric characters. Columns occupied by chord characters are
// preserved — if the chord line has no spare whitespace to give up, the run
// is only shortened as far as possible.
type Col = { char: string; type: SegmentType }

function flatten(line: PerformanceLine): Col[] {
  const out: Col[] = []
  for (const seg of line.segments) {
    for (const ch of seg.text) out.push({ char: ch, type: seg.type })
  }
  return out
}

function unflatten(cols: Col[]): PerformanceLine {
  const segments: PerformanceSegment[] = []
  let cur: PerformanceSegment | null = null
  for (const c of cols) {
    if (cur && cur.type === c.type) cur.text += c.char
    else { cur = { type: c.type, text: c.char }; segments.push(cur) }
  }
  return { segments }
}

function isChordOnly(line: PerformanceLine): boolean {
  let hasChord = false
  for (const seg of line.segments) {
    if (seg.type === 'chord') hasChord = true
    else if (seg.text.trim() !== '') return false
  }
  return hasChord
}

function isBlankLine(line: PerformanceLine): boolean {
  return line.segments.length === 0 ||
    line.segments.every(s => s.type === 'text' && s.text.trim() === '')
}

function shrinkPair(chordLine: PerformanceLine, lyricLine: PerformanceLine): [PerformanceLine, PerformanceLine] {
  const chord = flatten(chordLine)
  const lyric = flatten(lyricLine)
  const len = Math.max(chord.length, lyric.length)
  while (chord.length < len) chord.push({ char: ' ', type: 'text' })
  while (lyric.length < len) lyric.push({ char: ' ', type: 'text' })

  const drop = new Array<boolean>(len).fill(false)
  let i = 0
  while (i < len) {
    if (lyric[i]!.char === ' ') {
      let j = i
      while (j < len && lyric[j]!.char === ' ') j++
      const runLen = j - i
      if (runLen > 2) {
        // Columns inside the run where the chord row is also whitespace
        // — only those can be removed without disturbing chord positions.
        const removable: number[] = []
        for (let k = i; k < j; k++) if (chord[k]!.char === ' ') removable.push(k)
        const toRemove = Math.min(runLen - 2, removable.length)
        // Remove from the right side of the run so any chord chars in the
        // run keep their left-anchored position relative to the next word.
        for (let k = removable.length - toRemove; k < removable.length; k++) {
          drop[removable[k]!] = true
        }
      }
      i = j
    } else {
      i++
    }
  }

  const newChord: Col[] = []
  const newLyric: Col[] = []
  for (let k = 0; k < len; k++) {
    if (!drop[k]) {
      newChord.push(chord[k]!)
      newLyric.push(lyric[k]!)
    }
  }
  return [unflatten(newChord), unflatten(newLyric)]
}

function isLyricOnly(line: PerformanceLine): boolean {
  if (line.segments.length === 0) return false
  return line.segments.every(s => s.type === 'text')
}

function shrinkLyricOnly(line: PerformanceLine): PerformanceLine {
  // No chord row to keep aligned — collapse any 3+ run of whitespace to 2.
  const text = line.segments.map(s => s.text).join('').replace(/ {3,}/g, '  ')
  return { segments: [{ type: 'text', text }] }
}

function compressWhitespace(lines: PerformanceLine[]): PerformanceLine[] {
  const out: PerformanceLine[] = []
  let i = 0
  while (i < lines.length) {
    const a = lines[i]!
    const b = lines[i + 1]
    if (isChordOnly(a) && b && !isBlankLine(b) && !isChordOnly(b)) {
      const [na, nb] = shrinkPair(a, b)
      out.push(na, nb)
      i += 2
    } else if (isLyricOnly(a) && !isBlankLine(a)) {
      out.push(shrinkLyricOnly(a))
      i++
    } else {
      out.push(a)
      i++
    }
  }
  return out
}

// Parse all parts' performance into chord/lyric lines
const parsedContent = computed(() => {
  if (!song.value) return []
  return song.value.parts
    .filter(p => p.isActive)
    .map(p => ({ name: p.name, lines: compressWhitespace(parsePerformance(p.performance || null)) }))
})

watch(parsedContent, async () => {
  await nextTick()
  recomputeScale()
})

const startScrolling = () => {
  if (isPlaying.value) return
  isPlaying.value = true

  delayTimeout = setTimeout(() => {
    scrollAccumulator = 0
    scrollInterval = setInterval(() => {
      if (scrollContainer.value) {
        scrollAccumulator += speed.value * 0.25
        const whole = Math.floor(scrollAccumulator)
        if (whole > 0) {
          scrollContainer.value.scrollTop += whole
          scrollAccumulator -= whole
        }
        // Auto-stop at bottom
        const el = scrollContainer.value
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
          stopScrolling()
        }
      }
    }, 50)
  }, delay.value * 1000)
}

const stopScrolling = () => {
  isPlaying.value = false
  if (delayTimeout) { clearTimeout(delayTimeout); delayTimeout = null }
  if (scrollInterval) { clearInterval(scrollInterval); scrollInterval = null }
}

const rewind = () => {
  stopScrolling()
  if (scrollContainer.value) scrollContainer.value.scrollTop = 0
}

const adjustDelay = (delta: number) => {
  delay.value = Math.max(0, Math.min(60, delay.value + delta))
  saveTeleprompterSettings()
}

const adjustSpeed = (delta: number) => {
  speed.value = Math.max(1, Math.min(10, speed.value + delta))
  // If currently playing, restart with new speed
  if (isPlaying.value) {
    if (scrollInterval) clearInterval(scrollInterval)
    scrollAccumulator = 0
    scrollInterval = setInterval(() => {
      if (scrollContainer.value) {
        scrollAccumulator += speed.value * 0.25
        const whole = Math.floor(scrollAccumulator)
        if (whole > 0) {
          scrollContainer.value.scrollTop += whole
          scrollAccumulator -= whole
        }
        const el = scrollContainer.value
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
          stopScrolling()
        }
      }
    }, 50)
  }
  saveTeleprompterSettings()
}

let saveTimeout: ReturnType<typeof setTimeout> | null = null
const saveTeleprompterSettings = () => {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(async () => {
    if (!song.value) return
    try {
      await songStore.updateSong(song.value.id, {
        teleprompterDelay: delay.value,
        teleprompterSpeed: speed.value
      })
    } catch (e) {
      console.error('Failed to save teleprompter settings:', e)
    }
  }, 1000)
}

const handleBack = () => {
  stopListening()
  stopScrolling()
  emit('back')
}

// --- Lyrics matching ---
// Build a flat list of lyric words with their positions for matching
interface LyricWord {
  word: string          // lowercase, stripped of punctuation
  sectionIdx: number
  lineIdx: number
}

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim()

const lyricWords = computed<LyricWord[]>(() => {
  const words: LyricWord[] = []
  parsedContent.value.forEach((section, sIdx) => {
    section.lines.forEach((line, lIdx) => {
      for (const seg of line.segments) {
        if (seg.type !== 'text') continue
        for (const w of normalize(seg.text).split(/\s+/)) {
          if (w) words.push({ word: w, sectionIdx: sIdx, lineIdx: lIdx })
        }
      }
    })
  })
  return words
})

// How far through the lyrics we've matched (index into lyricWords).
// Everything before this is considered sung (including unmatched gaps).
const sungCursor = ref(0)

const sungLineKeys = computed(() => {
  const keys = new Set<string>()
  const words = lyricWords.value
  for (let i = 0; i < sungCursor.value && i < words.length; i++) {
    keys.add(`${words[i]!.sectionIdx}-${words[i]!.lineIdx}`)
  }
  return keys
})

const isLineSung = (sectionIdx: number, lineIdx: number) => {
  return sungLineKeys.value.has(`${sectionIdx}-${lineIdx}`)
}

// Try to match recognized words against lyrics from sungCursor forward.
// Only search a limited window ahead to avoid jumping to a repeated
// section (e.g. chorus) later in the song.
const LOOKAHEAD_WORDS = 90

const matchRecognized = (text: string) => {
  const recognized = normalize(text).split(/\s+/).filter(Boolean)
  if (recognized.length < 2) return // need at least 2 words to match

  const words = lyricWords.value
  const start = sungCursor.value
  const end = Math.min(words.length, start + LOOKAHEAD_WORDS)

  // Sliding window: find the first run of 2+ consecutive matching words.
  // Only advance the cursor to the start of the match — the words between
  // the old cursor and the match position are NOT marked as sung.
  for (let i = start; i < end; i++) {
    // Try matching recognized words starting from each position in lyrics.
    // Allow skipping recognized words (speech errors) but lyrics must be
    // strictly consecutive — stop at first lyric word that doesn't match
    // any remaining recognized word.
    let lyricPos = 0
    let recPos = 0
    while (recPos < recognized.length && (i + lyricPos) < end) {
      if (words[i + lyricPos]!.word === recognized[recPos]) {
        lyricPos++
        recPos++
      } else {
        // Skip this recognized word (misheard/extra word)
        recPos++
        // But limit skips — if we skip too many in a row it's not a real match
        let skipped = 1
        while (recPos < recognized.length && words[i + lyricPos]!.word !== recognized[recPos]) {
          recPos++
          skipped++
          if (skipped > 2) break
        }
      }
    }
    if (lyricPos >= 2) {
      sungCursor.value = i + lyricPos
      scrollToSungPosition()
      return
    }
  }
}

// Auto-scroll: if the last matched line is past the midpoint of the visible
// area, scroll down so it stays visible
const scrollToSungPosition = () => {
  const container = scrollContainer.value
  const content = contentEl.value
  if (!container || !content) return

  // Find the DOM element for the last sung line
  const cursor = sungCursor.value
  if (cursor <= 0 || cursor > lyricWords.value.length) return
  const lastSung = lyricWords.value[cursor - 1]!
  const lineEl = content.querySelector(`[data-line="${lastSung.sectionIdx}-${lastSung.lineIdx}"]`) as HTMLElement
  if (!lineEl) return

  // Scroll so the matched line sits at the vertical center of the container
  const lineTop = lineEl.offsetTop * scaleValue.value
  const lineHeight = lineEl.offsetHeight * scaleValue.value
  const targetScroll = lineTop + lineHeight / 2 - container.clientHeight / 2
  const clampedTarget = Math.max(0, Math.min(targetScroll, container.scrollHeight - container.clientHeight))

  if (Math.abs(container.scrollTop - clampedTarget) > lineHeight) {
    container.scrollTo({ top: clampedTarget, behavior: 'smooth' })
  }
}

// --- Speech recognition via Gemini Live API ---
import { LiveTranscriber } from '../../audio/LiveTranscriber'

const speechSupported = !!navigator.mediaDevices?.getUserMedia
const isListening = ref(false)
const listenText = ref('')
let transcriber: LiveTranscriber | null = null

const startListening = async () => {
  transcriber = new LiveTranscriber(
    (text, isFinal) => {
      listenText.value = text
      if (isFinal) {
        matchRecognized(text)
      }
    },
    async () => {
      if (!userStore.user) throw new Error('Not signed in')
      return await userStore.user.getIdToken()
    },
    song.value?.bandId
  )
  try {
    await transcriber.start()
    isListening.value = true
  } catch (e) {
    console.error('Failed to start transcription:', e)
    transcriber = null
  }
}

const stopListening = () => {
  isListening.value = false
  if (transcriber) {
    transcriber.stop()
    transcriber = null
  }
}

const toggleListening = () => {
  if (isListening.value) {
    stopListening()
  } else {
    listenText.value = ''
    sungCursor.value = 0
    startListening()
  }
}

// Lock page scroll while teleprompter overlay is mounted, otherwise the
// underlying page shows its own vertical scrollbar next to the inner one.
let savedScrollY = 0
const lockPageScroll = () => {
  savedScrollY = window.scrollY
  document.body.style.setProperty('position', 'fixed', 'important')
  document.body.style.setProperty('top', `-${savedScrollY}px`, 'important')
  document.body.style.setProperty('left', '0', 'important')
  document.body.style.setProperty('right', '0', 'important')
  document.body.style.setProperty('overflow', 'hidden', 'important')
  document.documentElement.style.setProperty('overflow', 'hidden', 'important')
}
const unlockPageScroll = () => {
  document.body.style.removeProperty('position')
  document.body.style.removeProperty('top')
  document.body.style.removeProperty('left')
  document.body.style.removeProperty('right')
  document.body.style.removeProperty('overflow')
  document.documentElement.style.removeProperty('overflow')
  window.scrollTo(0, savedScrollY)
}
lockPageScroll()

onUnmounted(() => {
  stopListening()
  stopScrolling()
  if (saveTimeout) clearTimeout(saveTimeout)
  if (resizeObs) { resizeObs.disconnect(); resizeObs = null }
  unlockPageScroll()
})
</script>

<template>
  <div class="fixed inset-0 z-50 bg-white flex flex-col">
    <!-- Loading -->
    <div v-if="loading || !song" class="flex-1 flex items-center justify-center">
      <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" fill="transparent" animationDuration=".5s" />
    </div>

    <template v-else>
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0">
        <div class="flex items-center gap-3">
          <Button icon="pi pi-arrow-left" text rounded severity="secondary" size="small" @click="handleBack" />
          <div>
            <h1 class="text-lg font-black text-slate-900 leading-tight">{{ song.title }}</h1>
            <p v-if="song.artist" class="text-xs text-slate-400">{{ song.artist }}</p>
          </div>
        </div>
      </div>

      <!-- Controls -->
      <div class="flex items-center justify-center gap-4 px-4 py-2 border-b border-slate-200 bg-slate-100 shrink-0">
        <!-- Delay -->
        <div class="flex flex-col items-center gap-0.5">
          <span class="text-[9px] font-bold text-slate-500 uppercase">Delay (s)</span>
          <div class="flex items-center gap-1">
            <Button icon="pi pi-chevron-down" text rounded size="small" class="!w-7 !h-7" @click="adjustDelay(-1)" />
            <span class="text-sm font-black text-slate-700 w-6 text-center">{{ delay }}</span>
            <Button icon="pi pi-chevron-up" text rounded size="small" class="!w-7 !h-7" @click="adjustDelay(1)" />
          </div>
        </div>

        <!-- Speed -->
        <div class="flex flex-col items-center gap-0.5">
          <span class="text-[9px] font-bold text-slate-500 uppercase">Speed</span>
          <div class="flex items-center gap-1">
            <Button icon="pi pi-chevron-down" text rounded size="small" class="!w-7 !h-7" @click="adjustSpeed(-1)" />
            <span class="text-sm font-black text-slate-700 w-6 text-center">{{ speed }}</span>
            <Button icon="pi pi-chevron-up" text rounded size="small" class="!w-7 !h-7" @click="adjustSpeed(1)" />
          </div>
        </div>

        <!-- Playback controls -->
        <div class="flex items-center gap-2">
          <Button icon="pi pi-replay" text rounded severity="secondary" size="small" v-tooltip.top="'Rewind'" @click="rewind" />
          <Button
            :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'"
            rounded
            severity="primary"
            size="small"
            class="!w-9 !h-9"
            v-tooltip.top="isPlaying ? 'Pause' : 'Play'"
            @click="isPlaying ? stopScrolling() : startScrolling()"
          />
        </div>
      </div>

      <!-- Listen Strip -->
      <div v-if="speechSupported" class="flex items-center gap-3 px-4 py-1.5 border-b border-slate-200 bg-white shrink-0">
        <Checkbox :modelValue="isListening" :binary="true" @update:modelValue="toggleListening" />
        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider shrink-0 cursor-pointer" @click="toggleListening">Listen</label>
        <div class="flex-1 relative">
          <input
            type="text"
            readonly
            :value="listenText"
            class="w-full text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 font-mono truncate"
            :class="isListening ? 'border-teal-400 bg-teal-50/30' : ''"
            placeholder="Speech will appear here..."
          />
          <i v-if="isListening" class="pi pi-microphone absolute right-2 top-1/2 -translate-y-1/2 text-teal-500 text-xs animate-pulse"></i>
        </div>
      </div>

      <!-- Scrollable Content. Content is rendered at natural monospace width
           (no wrap) then scaled via transform to fit the container width. -->
      <div ref="scrollContainer" class="flex-1 overflow-y-auto overflow-x-hidden px-4 py-6">
        <div class="relative" :style="{ height: `${scaledHeight}px` }">
          <div ref="contentEl" class="absolute top-0 left-0"
               :style="{ transformOrigin: 'top left', transform: `scale(${scaleValue})` }">
            <div v-for="(section, sIdx) in parsedContent" :key="sIdx" class="mb-8">
              <p class="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3">{{ section.name }}</p>
              <div class="font-mono">
                <div v-for="(line, lIdx) in section.lines" :key="lIdx"
                     :data-line="`${sIdx}-${lIdx}`"
                     :class="[
                       line.segments.length === 0 ? 'h-3' : 'leading-tight whitespace-pre text-[16px]',
                       isLineSung(sIdx, lIdx) ? 'opacity-40' : ''
                     ]">
                  <span v-for="(seg, segIdx) in line.segments" :key="segIdx"
                        :class="seg.type === 'chord' ? 'text-teal-600 font-bold' : 'text-slate-700'">{{ seg.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
